import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featured_image: string | null;
  category: string;
  author_id: string | null;
  reading_time_minutes: number;
  published_at: string | null;
  tags: string[] | null;
  profiles?: {
    username: string;
    avatar_id: number | null;
  } | null;
}

interface BlogCardProps {
  post: BlogPost;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  const avatarUrl = post.profiles?.avatar_id
    ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.profiles.avatar_id}`
    : `https://api.dicebear.com/7.x/avataaars/svg?seed=default`;

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-gradient-to-br from-white/5 to-white/10 border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,194,255,0.1)] hover:shadow-[0_0_40px_rgba(0,194,255,0.2)] hover:scale-[1.02] transition-all duration-300 backdrop-blur-md"
    >
      {/* Featured Image */}
      <div className="relative aspect-video overflow-hidden">
        {post.featured_image ? (
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#7000FF]/30 to-[#FF00A8]/30 flex items-center justify-center">
            <span className="text-4xl opacity-50">📝</span>
          </div>
        )}
        
        {/* Category Badge */}
        <Badge className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-[#00C2FF]/20 text-[#00C2FF] border-[#00C2FF]/30 backdrop-blur-sm text-xs sm:text-sm">
          {post.category}
        </Badge>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {/* Title */}
        <h2 className="text-lg sm:text-xl font-bold font-display text-white mb-3 line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-[#00C2FF] group-hover:via-[#7000FF] group-hover:to-[#FF00A8] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
          {post.title}
        </h2>

        {/* Excerpt */}
        <p className="text-gray-400 text-sm line-clamp-2 mb-4">
          {post.excerpt}
        </p>

        {/* Author & Meta */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src={avatarUrl}
              alt={post.profiles?.username || 'Author'}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 flex-shrink-0"
            />
            <p className="text-white text-sm font-medium">
              {post.profiles?.username || 'ThePromptSpace Team'}
            </p>
          </div>

          {/* Read Time */}
          <div className="flex items-center gap-1.5 text-white/50 text-xs">
            <Clock className="w-3.5 h-3.5" />
            <span>{post.reading_time_minutes} min</span>
          </div>
        </div>
      </div>
    </Link>
  );
};
